import React, { createContext, useContext, useState, useCallback } from 'react';
import { COLORS, RADIUS, SPACING, TYPOGRAPHY, SHADOWS, ANIMATION, Z_INDEX } from '../../config/branding';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * ToastProvider - Context provider for toast notifications
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration };

    setToasts((prev) => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback((message, duration) => addToast(message, 'success', duration), [addToast]);
  const error = useCallback((message, duration) => addToast(message, 'error', duration), [addToast]);
  const warning = useCallback((message, duration) => addToast(message, 'warning', duration), [addToast]);
  const info = useCallback((message, duration) => addToast(message, 'info', duration), [addToast]);

  return (
    <ToastContext.Provider value={{ success, error, warning, info, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

/**
 * ToastContainer - Displays toast notifications
 */
const ToastContainer = ({ toasts, removeToast }) => {
  const containerStyle = {
    position: 'fixed',
    top: SPACING.lg,
    right: SPACING.lg,
    zIndex: Z_INDEX.tooltip,
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.sm,
    maxWidth: '400px',
  };

  return (
    <div style={containerStyle}>
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

/**
 * Toast - Individual toast notification
 */
const Toast = ({ toast, onClose }) => {
  const typeConfig = {
    success: {
      backgroundColor: COLORS.successLight,
      borderColor: COLORS.success,
      textColor: COLORS.gray[800],
      icon: '✓',
    },
    error: {
      backgroundColor: COLORS.errorLight,
      borderColor: COLORS.error,
      textColor: COLORS.gray[800],
      icon: '✕',
    },
    warning: {
      backgroundColor: COLORS.warningLight,
      borderColor: COLORS.warning,
      textColor: COLORS.gray[800],
      icon: '⚠',
    },
    info: {
      backgroundColor: COLORS.infoLight,
      borderColor: COLORS.info,
      textColor: COLORS.gray[800],
      icon: 'ℹ',
    },
  };

  const config = typeConfig[toast.type] || typeConfig.info;

  const toastStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: SPACING.md,
    padding: SPACING.md,
    backgroundColor: config.backgroundColor,
    borderLeft: `4px solid ${config.borderColor}`,
    borderRadius: RADIUS.lg,
    boxShadow: SHADOWS.lg,
    fontSize: TYPOGRAPHY.fontSize.sm,
    fontWeight: TYPOGRAPHY.fontWeight.medium,
    color: config.textColor,
    animation: `slideIn ${ANIMATION.normal} ease-out`,
    minWidth: '300px',
  };

  const iconStyle = {
    fontSize: TYPOGRAPHY.fontSize.lg,
    fontWeight: TYPOGRAPHY.fontWeight.bold,
    color: config.borderColor,
    flexShrink: 0,
  };

  const messageStyle = {
    flex: 1,
    wordBreak: 'break-word',
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: TYPOGRAPHY.fontSize.lg,
    color: COLORS.gray[500],
    padding: 0,
    marginLeft: SPACING.sm,
    flexShrink: 0,
    lineHeight: 1,
  };

  return (
    <div style={toastStyle}>
      <span style={iconStyle}>{config.icon}</span>
      <div style={messageStyle}>{toast.message}</div>
      <button style={closeButtonStyle} onClick={onClose} aria-label="Close">
        ×
      </button>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ToastProvider;
